import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import TextInput from "../../components/formInputs/TextInput";
import Button from "../../components/buttons/Button";
import ArrowLeftIcon from "../../components/icons/ArrowLeftIcon";
import "../../components/styles/paymentPage.scss";

/**
 * PaymentPage — Payment Details (Onboarding Step 3)
 * Figma: 1.3 Onboarding (Desktop) (24654:6569)
 *
 * Layout: Split — left summary (944px) | right form (976px, bg #0b0b0f)
 *
 * Left: Subscription summary
 *   - "Subscription summary" (24px/600)
 *   - Plan row: "Streamline Premium" / "KAVA - N1,500/mo" | "NGN 149.000"
 *   - Divider
 *   - Tax row: "Tax" (with ? icon) | "NGN 0"
 *   - "Add promo code" button (ghost)
 *   - Divider
 *   - Subtotal: "Subtotal" | "NGN 1.788.000"
 *
 * Right: Payment form (715px, centered)
 *   - Back arrow, "Payment details" (Bold 36px)
 *   - "Payment methods" (24px/600)
 *   - 2-column rows: Name on card | Expiry | Card number | CVV
 *   - Country dropdown
 *   - "Pay now" button (full width)
 *   - Disclaimer text
 *   - Footer links
 */

const PaymentPage = () => {
  const navigate = useNavigate();
  const [nameOnCard, setNameOnCard] = useState("Dean");
  const [expiryDate, setExpiryDate] = useState("04 / 29");
  const [cardNumber, setCardNumber] = useState("123 456 789 101 123");
  const [cvv, setCvv] = useState("");
  const [country, setCountry] = useState("Indonesia");
  const [isLoading, setIsLoading] = useState(false);

  const handlePay = useCallback(() => {
    if (isLoading) return;
    setIsLoading(true);

    // TODO: Replace with actual payment API call
    setTimeout(() => {
      setIsLoading(false);
      navigate("/onboarding/profiles");
    }, 2000);
  }, [isLoading, navigate]);

  return (
    <div className="payment-page">
      {/* ===== LEFT: Subscription Summary ===== */}
      <div className="payment-page__summary">
        <h2 className="payment-page__summary-title">Subscription summary</h2>

        <div className="payment-page__summary-details">
          {/* Plan row */}
          <div className="payment-page__row">
            <div className="payment-page__row-left">
              <span className="payment-page__plan-name">Streamline Premium</span>
              <span className="payment-page__plan-tier">KAVA - N1,500/mo</span>
            </div>
            <span className="payment-page__row-price">NGN 149.000</span>
          </div>

          {/* Divider */}
          <hr className="payment-page__divider" />

          {/* Tax row */}
          <div className="payment-page__row">
            <div className="payment-page__tax-label">
              <span className="payment-page__tax-text">Tax</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="payment-page__tax-icon">
                <circle cx="10" cy="10" r="8.33" stroke="#737373" strokeWidth="1.5" />
                <text x="10" y="14" textAnchor="middle" fill="#737373" fontSize="12" fontFamily="Poppins">?</text>
              </svg>
            </div>
            <span className="payment-page__row-price">NGN 0</span>
          </div>

          {/* Promo code button */}
          <Button
            label="Add promo code"
            variant="secondary"
            size="sm"
            className="payment-page__promo-btn"
          />

          {/* Divider */}
          <hr className="payment-page__divider" />

          {/* Subtotal */}
          <div className="payment-page__row">
            <span className="payment-page__subtotal-label">Subtotal</span>
            <span className="payment-page__subtotal-value">NGN 1.788.000</span>
          </div>
        </div>
      </div>

      {/* ===== RIGHT: Payment Form ===== */}
      <div className="payment-page__form-area">
        {/* Background pattern */}
        <div className="payment-page__bg-pattern">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="payment-grid" width="80" height="80" patternUnits="userSpaceOnUse">
                <path d="M 80 0 L 0 0 0 80" fill="none" stroke="#1a1a1e" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#payment-grid)" />
          </svg>
        </div>

        {/* Form wrapper */}
        <div className="payment-page__form-wrapper">
          {/* Back arrow */}
          <button
            className="payment-page__back"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowLeftIcon size={36} color="#fafafa" />
          </button>

          {/* Title */}
          <h1 className="payment-page__title">Payment details</h1>

          {/* Form content */}
          <div className="payment-page__form-content">
            <h3 className="payment-page__section-title">Payment methods</h3>

            <div className="payment-page__form-group">
              {/* Form fields */}
              <div className="payment-page__form-fields">
                {/* Row 1: Name on card | Expiry date */}
                <div className="payment-page__field-row">
                  <TextInput
                    label="Name on card"
                    value={nameOnCard}
                    onChange={setNameOnCard}
                    placeholder="Enter name"
                  />
                  <TextInput
                    label="Expiry date"
                    value={expiryDate}
                    onChange={setExpiryDate}
                    placeholder="MM / YY"
                  />
                </div>

                {/* Row 2: Card number | CVV */}
                <div className="payment-page__field-row">
                  <TextInput
                    label="Card number"
                    value={cardNumber}
                    onChange={setCardNumber}
                    placeholder="Enter card number"
                  />
                  <TextInput
                    label="CVV"
                    value={cvv}
                    onChange={setCvv}
                    placeholder="XXXX"
                  />
                </div>

                {/* Country dropdown (simplified as TextInput for now) */}
                <TextInput
                  label="Country"
                  value={country}
                  onChange={setCountry}
                  placeholder="Select country"
                />
              </div>

              {/* Pay now button */}
              <Button
                label="Pay now"
                variant="primary"
                size="md"
                isLoading={isLoading}
                action={handlePay}
              />

              {/* Disclaimer */}
              <p className="payment-page__disclaimer">
                By confirming your subscription, you allow StreamLine to charge
                your card for this payment and future payments in accordance with
                our terms. You can always cancel your subscription
              </p>
            </div>
          </div>
        </div>

        {/* Footer links */}
        <nav className="payment-page__footer">
          <a href="/help" className="payment-page__link">Help Center</a>
          <span className="payment-page__link-divider" />
          <a href="/terms" className="payment-page__link">Terms & Condition</a>
          <span className="payment-page__link-divider" />
          <a href="/privacy" className="payment-page__link">Privacy Policy</a>
          <span className="payment-page__link-divider" />
          <a href="/contact" className="payment-page__link">Contact us</a>
        </nav>
      </div>
    </div>
  );
};

export default PaymentPage;
